/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import * as Msdyn365 from '@msdyn365-commerce/core';
import { IDlvryQuestbackData } from './dlvry-questback.data';
import { IDlvryQuestbackProps } from './dlvry-questback.props.autogenerated';
import { GetCookie, SetCookie } from '../../shared-utils/cookie-utilities';

export interface IDlvryQuestbackViewProps extends IDlvryQuestbackProps<IDlvryQuestbackData> {
    baseClassName: string;
    popupId: string;
    popupClassName: string;
    modalContentClassName: string;
    modalContentCloseButton: React.ReactElement;
    questBackIFrame: React.ReactNode;
}

/**
 *
 * DlvryQuestback component
 * @extends {React.PureComponent<IDlvryQuestbackProps<IDlvryQuestbackData>>}
 */
class DlvryQuestback extends React.PureComponent<IDlvryQuestbackProps<IDlvryQuestbackData>> {
    private _baseClassName: string = 'dlvry-questback';
    private _popupId: string = `${this._baseClassName}__questback-popup`;
    private _questBackCookieName: string = 'questback-survey-v2';

    public componentDidMount(): void {
        const { context, config } = this.props;
        const hidePopup = GetCookie(context.request.cookies, this._questBackCookieName);

        if (config.disableModule || !context.request.user.isAuthenticated || context.request.params.isEditor || hidePopup === '1') {
            return;
        }

        const oboCustomerAccountNumber: { can: string | undefined } = Msdyn365.getCustomerAccountNumber(context.request) as any;

        // Don't show popup yet if logged in as OBO, but no account has been selected yet
        if (context.request.user.isOBORequest && !oboCustomerAccountNumber.can) {
            return;
        }

        setTimeout(async () => {
            const popup = document.getElementById(this._popupId);

            // Wait for consent to be hidden before showing the popup
            while (window.consentVisible) {
                await new Promise(resolve => setTimeout(resolve, 250));
            }

            if (popup) {
                popup.style.display = 'block';
            }
        }, 2000);
    }

    public render(): JSX.Element | null {
        const { config, context, resources } = this.props;

        if (config.disableModule) {
            return null;
        }

        const viewProps: IDlvryQuestbackViewProps = {
            ...this.props,
            baseClassName: this._baseClassName,
            popupId: this._popupId,
            popupClassName: `${this._baseClassName}__modal`,
            modalContentClassName: `${this._baseClassName}__modal-content`,
            modalContentCloseButton: (
                <span className={`${this._baseClassName}__modal-content__close-button`} onClick={this.closePopup}>&times;</span>
            ),
            questBackIFrame: context.request.user.isAuthenticated
                ? <iframe src={this.populateCustomerInformation(config.questBackURL)} title={resources.questBackTitle} />
                : undefined
        };

        return this.props.renderView(viewProps);
    }

    private closePopup = (): void => {
        const popup = document.getElementById(this._popupId);
        if (popup) {
            popup.style.display = 'none';
        }
        SetCookie(this.props.context.request.cookies, '1', this._questBackCookieName, 604800, true);
    };

    private populateCustomerInformation = (questBackURL: string | undefined): string | undefined => {
        if (!questBackURL) {
            return questBackURL;
        }

        const { context } = this.props;
        const { customerInformation, organizationInformation } = this.props.data;
        const url = new URL(questBackURL);

        if (organizationInformation.result?.AccountNumber) {
            url.searchParams.append('Kundenummer', organizationInformation.result.AccountNumber);
        }


        if (customerInformation.result?.Phone) {
            url.searchParams.append('Telefonnummer', customerInformation.result.Phone);
        }

        if (customerInformation.result?.Name) {
            url.searchParams.append('Brukernavn', customerInformation.result.Name);
        }

        url.searchParams.append('Grossist', context.request.apiSettings.oun);

        return url.toString();
    };
}

export default DlvryQuestback;
